// Source of truth for «بوصلة د. سارة» v1 — instruments.config JSON.
// All Arabic strings are FROZEN (verbatim from the v1 content & scoring pack).
// Item ids are FROZEN — they are the contract for the scorer.
//
// Loaded into instruments.config via the seed migration and later consumed by:
//  - the item player (Phase 2)
//  - src/lib/bousala/scoring.ts (Phase 3)
//  - the report renderer + AI narrative fn (Phase 4)

export type ItemType = "likert5" | "importance5" | "scenario" | "pair" | "top3" | "iq";

export interface WeightMap {
  [scale: string]: number;
}
export interface Option {
  id: string;
  label_ar: string;
  weights?: WeightMap;
}
export interface Item {
  id: string;
  type: ItemType;
  text_ar: string;
  weights?: WeightMap;
  reverse?: boolean;
  options?: Option[];
  max_select?: number;
  validity?: string; // "attention:<v>" | "desirability" | "consistency:<twin_id>"
  stem_ar?: string; // for importance5, shown once at section top
  correct?: string; // option id — used for type "iq" only
}
export interface Section {
  id: string;
  title_ar: string;
  intro_ar: string;
  note_ar?: string;
  items: Item[];
}

// ---- Fixed label sets (Part 1.1) ----
export const LIKERT_LABELS = [
  "لا يشبهني أبداً",
  "يشبهني قليلاً",
  "يشبهني أحياناً",
  "يشبهني غالباً",
  "يشبهني جداً",
];
export const IMPORTANCE_LABELS = [
  "غير مهم أبداً",
  "قليل الأهمية",
  "متوسط الأهمية",
  "مهم",
  "مهم جداً",
];

// ---- Scales registry (Part 3) ----
export const SCALES: Record<string, { axis: string; label_ar: string }> = {
  // intelligences
  ling:   { axis: "intelligences", label_ar: "لغوي" },
  logic:  { axis: "intelligences", label_ar: "منطقي رياضي" },
  visual: { axis: "intelligences", label_ar: "بصري مكاني" },
  kines:  { axis: "intelligences", label_ar: "جسدي حركي" },
  music:  { axis: "intelligences", label_ar: "موسيقي إيقاعي" },
  social: { axis: "intelligences", label_ar: "اجتماعي" },
  intra:  { axis: "intelligences", label_ar: "ذاتي" },
  natur:  { axis: "intelligences", label_ar: "طبيعي" },
  exist:  { axis: "intelligences", label_ar: "وجودي فلسفي" },
  // interests (Holland/RIASEC)
  R: { axis: "interests", label_ar: "عملي تطبيقي" },
  I: { axis: "interests", label_ar: "تحليلي باحث" },
  A: { axis: "interests", label_ar: "إبداعي تعبيري" },
  S: { axis: "interests", label_ar: "إنساني اجتماعي" },
  E: { axis: "interests", label_ar: "قيادي مبادر" },
  C: { axis: "interests", label_ar: "منظم دقيق" },
  // personality (consolidated to 7)
  open:     { axis: "personality", label_ar: "الانفتاح على التجربة" },
  consc:    { axis: "personality", label_ar: "الانضباط والمسؤولية" },
  extra:    { axis: "personality", label_ar: "التواصل الاجتماعي" },
  empath:   { axis: "personality", label_ar: "التعاطف والتعاون" },
  resil:    { axis: "personality", label_ar: "تحمّل الضغط والمرونة" },
  autonomy: { axis: "personality", label_ar: "استقلالية القرار" },
  conf:     { axis: "personality", label_ar: "الثقة وتقبّل الخطأ" },
  // skills
  comm:      { axis: "skills", label_ar: "التواصل" },
  org:       { axis: "skills", label_ar: "التنظيم وإدارة الوقت" },
  prob:      { axis: "skills", label_ar: "حل المشكلات" },
  team:      { axis: "skills", label_ar: "العمل الجماعي" },
  selflearn: { axis: "skills", label_ar: "التعلم الذاتي" },
  digital:   { axis: "skills", label_ar: "المهارات الرقمية" },
  decide:    { axis: "skills", label_ar: "اتخاذ القرار" },
  persist:   { axis: "skills", label_ar: "المثابرة" },
  // values
  v_security:  { axis: "values", label_ar: "الاستقرار" },
  v_income:    { axis: "values", label_ar: "الدخل" },
  v_status:    { axis: "values", label_ar: "المكانة" },
  v_help:      { axis: "values", label_ar: "مساعدة الناس" },
  v_freedom:   { axis: "values", label_ar: "الحرية" },
  v_creative:  { axis: "values", label_ar: "الإبداع" },
  v_lead:      { axis: "values", label_ar: "القيادة والتأثير" },
  v_know:      { axis: "values", label_ar: "المعرفة" },
  v_comfort:   { axis: "values", label_ar: "الراحة والتوازن" },
  v_challenge: { axis: "values", label_ar: "التحدي" },
  // learning
  l_verbal:    { axis: "learning", label_ar: "سمعي لفظي" },
  l_visual:    { axis: "learning", label_ar: "بصري" },
  l_practical: { axis: "learning", label_ar: "عملي تجريبي" },
  l_social:    { axis: "learning", label_ar: "اجتماعي نقاشي" },
  // readiness positive
  ready_know: { axis: "readiness", label_ar: "معرفة الذات والتخصصات" },
  ready_own:  { axis: "readiness", label_ar: "تحمّل مسؤولية القرار" },
  // logical / IQ
  iq: { axis: "intelligences_plus", label_ar: "التفكير المنطقي والذكاء" },
  // readiness risk flags
  flag_parent:    { axis: "flags", label_ar: "ضغط الأهل" },
  flag_peer:      { axis: "flags", label_ar: "تقليد الأصدقاء" },
  flag_status:    { axis: "flags", label_ar: "حب الصورة الاجتماعية" },
  flag_confusion: { axis: "flags", label_ar: "الخلط بين المادة والمهنة" },
  flag_anxiety:   { axis: "flags", label_ar: "قلق القرار" },
};

// ---------- Helpers ----------
const L = (
  id: string,
  text_ar: string,
  weights: WeightMap,
  reverse = false,
): Item => ({ id, type: "likert5", text_ar, weights, ...(reverse ? { reverse: true } : {}) });

const V = (id: string, text_ar: string, validity: string): Item => ({
  id,
  type: "likert5",
  text_ar,
  validity,
});

const SC = (
  id: string,
  text_ar: string,
  options: { label_ar: string; weights: WeightMap }[],
  type: "scenario" | "pair" = "scenario",
): Item => ({
  id,
  type,
  text_ar,
  options: options.map((o, i) => ({ id: `${id}o${i + 1}`, label_ar: o.label_ar, weights: o.weights })),
});

// IQ / logic item helper — one correct option, no scale weights.
// `correctIndex` is 1-based (matches option id suffix `${id}o${i+1}`).
const IQ = (
  id: string,
  text_ar: string,
  options: string[],
  correctIndex: number,
): Item => ({
  id,
  type: "iq",
  text_ar,
  options: options.map((label, i) => ({ id: `${id}o${i + 1}`, label_ar: label })),
  correct: `${id}o${correctIndex}`,
});

// ---------- Pre-test mini-profile (Part 4.5) — stored on attempts.pre_profile ----------
export const PRE_PROFILE: {
  id: string;
  text_ar: string;
  options: { id: string; label_ar: string }[];
}[] = [
  {
    id: "study_status",
    text_ar: "وين أنت اليوم دراسياً؟",
    options: [
      { id: "bac",        label_ar: "بكالوريا" },
      { id: "uni",        label_ar: "جامعة" },
      { id: "graduated",  label_ar: "متخرج" },
      { id: "working",    label_ar: "منهي دراستي وأعمل" },
      { id: "other",      label_ar: "نظام دراسي آخر" },
    ],
  },
  {
    id: "stream",
    text_ar: "فرعك أو مسارك الدراسي؟",
    options: [
      { id: "علمي",       label_ar: "علمي" },
      { id: "أدبي",       label_ar: "أدبي" },
      { id: "لا ينطبق",   label_ar: "لا ينطبق (نظام آخر / IB / خارج البلد)" },
    ],
  },
  {
    id: "region_now",
    text_ar: "وين ناوي تدرس غالباً؟",
    options: [
      { id: "shaam",  label_ar: "سوريا أو بلاد الشام" },
      { id: "gulf",   label_ar: "الخليج" },
      { id: "eu",     label_ar: "أوروبا أو خارج المنطقة" },
      { id: "unsure", label_ar: "لسّا ما قررت" },
    ],
  },
];

// ==================================================================
// SECTIONS (Part 4)
// ==================================================================

// ---- Section A — الذكاءات المتعددة (40 items) ----
const SECTION_A: Section = {
  id: "A",
  title_ar: "الذكاءات المتعددة",
  intro_ar: "المحور الأول: كيف يفكر عقلك؟ — ما في حدا «أذكى من حدا»… في عقول بتشتغل بطرق مختلفة. خلينا نشوف وين بتلمع أنت.",
  items: [
    { id: "A1", type: "likert5", text_ar: "لما بيوصلك موضوع جديد — درس، خبر، فكرة عالسوشال — ما بتحس إنك فهمته منيح إلا لما تقرا عنه أو تسمع حدا يشرحه بكلمات واضحة.", weights: { ling: 1 } },
    { id: "A2", type: "likert5", text_ar: "رفيقك استصعب فكرة قبل الامتحان أو بالشغل — بتعرف كيف تبسّطها له وتشرحها بطريقة أوضح.", weights: { ling: 1, comm: 0.5 } },
    { id: "A3", type: "likert5", text_ar: "بجلسة مع الشلة أو نقاش بالقروب، أحلى لحظة عندك هي لما تعبّر عن رأيك وتدافع عن فكرتك.", weights: { ling: 1, extra: 0.5 } },
    { id: "A4", type: "likert5", text_ar: "وأنت عم تسمع حدا يحكي — أستاذ، صديق، أو بودكاست — بتنتبه لنبرة صوته وللكلمات يلي بيختارها.", weights: { ling: 1 } },
    { id: "A5", type: "likert5", text_ar: "لما تصير مشكلة — موبايلك علّق أو خطة الطلعة خربت — أول شي بتدوّر على السبب قبل ما تجرّب حلول عشوائية.", weights: { logic: 1, prob: 0.5 } },
    { id: "A6", type: "likert5", text_ar: "الأسئلة والألغاز يلي بدها تحليل واكتشاف أنماط متشابهة أو مختلفة بتشدّك — حتى لو كانت مجرد تحدٍّ عالسوشال ميديا.", weights: { logic: 1 } },
    { id: "A7", type: "likert5", text_ar: "لما حدا يشرح خطة أو يحكي قصة، بتلاحظ بسرعة إذا في تناقض أو تفصيل ما بيركب مع باقي الكلام.", weights: { logic: 1 } },
    { id: "A8", type: "likert5", text_ar: "بتستمتع بألعاب الاستراتيجية والألغاز — شطرنج، سودوكو، أو مراحل التخطيط بألعاب الفيديو.", weights: { logic: 1 } },
    { id: "A9", type: "likert5", text_ar: "لما الشرح يجي على شكل خريطة ذهنية أو إنفوغرافيك أو رسمة، بتفهم أسرع بكتير من النص الطويل.", weights: { visual: 1, l_visual: 0.5 } },
    { id: "A10", type: "likert5", text_ar: "بتتذكر وجه الشخص ووين قعدتوا وشكل المكان بالتفصيل… بس اسمه بيروح من بالك.", weights: { visual: 1 } },
    { id: "A11", type: "likert5", text_ar: "قبل ما تبلّش بأي شي — ترتيب غرفتك، تصميم بوست، مشروع — بتقدر تتخيّل شكله النهائي براسك.", weights: { visual: 1 } },
    { id: "A12", type: "likert5", text_ar: "بتلاحظ فوراً إذا الألوان مش متناسقة بستوري أو عرض تقديمي، والتفاصيل البصرية بتلفت نظرك.", weights: { visual: 1 } },
    { id: "V1", type: "likert5", text_ar: "سؤال تأكيد بسيط: لنتأكد إنك عم تقرا الأسئلة، اختار هون «يشبهني أحياناً».", validity: "attention:3" },
    { id: "A13", type: "likert5", text_ar: "الشغلة ما بتثبت براسك إلا لما تجرّبها بإيدك — وصفة، جهاز جديد، تمرين.", weights: { kines: 1, l_practical: 0.5 } },
    { id: "A14", type: "likert5", text_ar: "بمحاضرة أو اجتماع كله قعدة وسماع، بتحس إنك عم تفقد تركيزك وبتبلّش تتململ.", weights: { kines: 1 } },
    { id: "A15", type: "likert5", text_ar: "بتتعلم حركة أو مهارة عملية بسرعة لما تشوفها قدامك وتقلّدها — تمرين جيم، وصفة، حرفة.", weights: { kines: 1 } },
    { id: "A16", type: "likert5", text_ar: "بيساعدك إنك تتحرك أو تمسك شي بإيدك وأنت عم تدرس أو تفكر — تمشي بالغرفة، تلعب بقلم.", weights: { kines: 1 } },
    { id: "A17", type: "likert5", text_ar: "أغنية بتسمعها مرتين بتصير تحفظها، والجمل المسموعة بتعلق براسك بسهولة.", weights: { music: 1 } },
    { id: "A18", type: "likert5", text_ar: "من نبرة صوت الشخص عالتلفون بتعرف إذا هو مبسوط أو مضايق — حتى لو قال «تمام».", weights: { music: 1, social: 0.5 } },
    { id: "A19", type: "likert5", text_ar: "لما بدك تحفظ شي، بتقراه بصوت عالي أو بتسمعه وبتكرره — وهيك بيثبت.", weights: { music: 1, l_verbal: 0.5 } },
    { id: "A20", type: "likert5", text_ar: "إذا في صوت نشاز أو إيقاع مش مضبوط بأغنية أو فيديو، بتحس فيه فوراً.", weights: { music: 1 } },
    { id: "A21", type: "likert5", text_ar: "من أول ما يفوت رفيقك أو يكتب رسالة بالقروب، بتحس إذا مو على طبيعته.", weights: { social: 1, empath: 0.5 } },
    { id: "A22", type: "likert5", text_ar: "لما حدا من الرفقات يمرق بظرف صعب أو يحتار بقرار، غالباً أنت أول واحد بيتصل فيه.", weights: { social: 1 } },
    { id: "A23", type: "likert5", text_ar: "لما يولّع خلاف بالقروب أو بين رفقات، بتعرف كيف تهدّي الجو وتقرّب وجهات النظر.", weights: { social: 1, comm: 0.5 } },
    { id: "A24", type: "likert5", text_ar: "بتحس شو محتاج الشخص يلي قدامك — دعم، سكوت، أو نصيحة — حتى لو ما طلب.", weights: { social: 1 } },
    { id: "A25", type: "likert5", text_ar: "بعد يوم مليان ناس وأحداث، بتحتاج وقت لحالك لترتّب مشاعرك وتفهم شو بدك.", weights: { intra: 1 } },
    { id: "A26", type: "likert5", text_ar: "بعد موقف قوي — خناقة، امتحان، قرار — بترجع تراجع تصرفك وتسأل حالك ليش تصرفت هيك.", weights: { intra: 1 } },
    { id: "A27", type: "likert5", text_ar: "لما الكل حواليك يقلّك «لازم تعمل هيك»، بتعرف تفرّق بين الشي يلي أنت بدك ياه، والشي يلي الآخرين متوقعينه منك.", weights: { intra: 1, autonomy: 0.5 } },
    { id: "A28", type: "likert5", text_ar: "بتفكر كتير بمستقبلك وخياراتك: لوين رايح؟ وشو معنى يلي عم تعمله؟", weights: { intra: 1, exist: 0.5 } },
    { id: "A29", type: "likert5", text_ar: "فيديو عن الفضاء أو البحر أو جسم الإنسان بيخلّيك توقف التصفّح وتكمّل تتفرّج — الطبيعة والحيوانات والنباتات بتشدّك.", weights: { natur: 1 } },
    { id: "A30", type: "likert5", text_ar: "مواضيع متل الأحياء والصحة والتغذية والبيئة بتجذبك أكتر من غيرها.", weights: { natur: 1 } },
    { id: "A31", type: "likert5", text_ar: "بتحب تصنّف الأشياء وتلاحظ الفروق بين الأنواع والأنماط — نباتات، سيارات، شخصيات، أي شي.", weights: { natur: 1, logic: 0.5 } },
    { id: "A32", type: "likert5", text_ar: "لما تطلع عالطبيعة — بحر، جبل، مزرعة — بتحس براحة أو فضول ما بتحسه بالمدينة.", weights: { natur: 1 } },
    { id: "A33", type: "likert5", text_ar: "بينك وبين حالك، بتسأل أسئلة عميقة عن الحياة والنجاح والمعنى.", weights: { exist: 1 } },
    { id: "A34", type: "likert5", text_ar: "الجواب السطحي ما بيكفيك — بتضل تسأل «ليش؟» لحتى توصل عالسبب والمعنى.", weights: { exist: 1, logic: 0.5 } },
    { id: "A35", type: "likert5", text_ar: "بيهمك دراستك أو شغلك يكون إله معنى — مش بس شهادة عالحيط أو معاش آخر الشهر.", weights: { exist: 1 } },
    { id: "A36", type: "likert5", text_ar: "سهرة فيها نقاش عميق واحد لواحد أحلى عندك من مية حديث سريع وسطحي.", weights: { exist: 1 } },
    {
      id: "A37", type: "scenario", text_ar: "عندكم مشروع مهم — بالمدرسة أو الجامعة أو الشغل — والفريق فوضى وما حدا عارف من وين يبلّش. أي دور بتلاقي حالك أخدته غالباً؟",
      options: [
      { id: "A37o1", label_ar: "بمسك الكتابة: بصيغ الأفكار والكلام النهائي.", weights: { ling: 2 } },
      { id: "A37o2", label_ar: "بحط خطة واضحة وبقسّم الشغل خطوات ومهام.", weights: { logic: 2, org: 1 } },
      { id: "A37o3", label_ar: "بصمّم العرض والشكل البصري: السلايدات والهوية.", weights: { visual: 2 } },
      { id: "A37o4", label_ar: "بنفّذ الجزء العملي بإيدي وبتحرك عالأرض.", weights: { kines: 2 } },
      { id: "A37o5", label_ar: "برفع معنويات الفريق وبخلّي الكل يشارك.", weights: { social: 2, E: 1 } },
      { id: "A37o6", label_ar: "بفكّر: شو رسالة المشروع وشو المعنى يلي بدنا نوصله؟", weights: { exist: 2 } },
      ],
    },
    {
      id: "A38", type: "scenario", text_ar: "شفت شرح لموضوع صعب — محاضرة أو فيديو يوتيوب — وما فهمته منيح. شو أول شي بتعمله غالباً؟",
      options: [
      { id: "A38o1", label_ar: "بدوّر على شرح تاني بالكلمات أو مقال مكتوب.", weights: { ling: 2, l_verbal: 1 } },
      { id: "A38o2", label_ar: "بدوّر على القاعدة أو المنطق يلي ورا الفكرة.", weights: { logic: 2 } },
      { id: "A38o3", label_ar: "برسم خريطة ذهنية أو مخطط لرتّب الفكرة.", weights: { visual: 2, l_visual: 1 } },
      { id: "A38o4", label_ar: "لازم جرّبه بإيدي: مثال، تمرين، تطبيق.", weights: { kines: 2, l_practical: 1 } },
      { id: "A38o5", label_ar: "بفتح الموضوع مع رفيق ومنناقشه سوا.", weights: { social: 2, l_social: 1 } },
      { id: "A38o6", label_ar: "بسأل حالي: ليش عم ندرس هالشي أصلاً؟", weights: { exist: 2 } },
      ],
    },
    {
      id: "A39", type: "scenario", text_ar: "رفيقك المقرّب صار له يومين عم يرد بطريقة باردة عالرسائل وعم يحط ستوريات حزينة، بس ما عم يحكي شي. شو بتعمل غالباً؟",
      options: [
      { id: "A39o1", label_ar: "بفتح معه حديث لطيف وبخلّي الحكي يمشي لحاله.", weights: { social: 2, ling: 1 } },
      { id: "A39o2", label_ar: "بفكّر شو ممكن يكون صار معه بآخر فترة لحتى افهمه أكتر.", weights: { logic: 2 } },
      { id: "A39o3", label_ar: "براقب ملامحه وطريقة قعدته وستورياته لفهم أكتر.", weights: { visual: 1, social: 1 } },
      { id: "A39o4", label_ar: "بقترح نطلع مشوار أو نعمل شي سوا يغيّر الجو.", weights: { kines: 2 } },
      { id: "A39o5", label_ar: "بضل جنبه بصمت وبخلّيه يحس إني موجود لما يجهز.", weights: { intra: 1, empath: 1 } },
      { id: "A39o6", label_ar: "بفكّر بعمق: ليش صعب على الناس يعبّروا عن وجعهم؟", weights: { exist: 2 } },
      ],
    },
  ],
};

// ---- Section B — الميول المهنية (30 items) ----
const SECTION_B: Section = {
  id: "B",
  title_ar: "الميول المهنية",
  intro_ar: "المحور الثاني: شو بيجذبك؟ — مو شو بتحفظ أحسن… شو بتحب تعمل فعلاً.",
  items: [
    { id: "B1", type: "likert5", text_ar: "بتحب الشغل يلي بتشتغل فيه بإيدك وبأدوات حقيقية — تركيب، تصليح، تجهيز — مش بس ورا شاشة وورق.", weights: { R: 1 } },
    { id: "B2", type: "likert5", text_ar: "بتفضّل تتعلم بالتجربة المباشرة: «عطيني ياها بإيدي» أحسن من ساعة شرح نظري.", weights: { R: 1, kines: 0.3 } },
    { id: "B3", type: "likert5", text_ar: "أحلى إحساس عندك لما تشوف نتيجة شغلك قدام عينك — شي خلص وصار جاهز وملموس.", weights: { R: 1 } },
    { id: "B4", type: "likert5", text_ar: "لما تقارن التخصصات، بتشدّك يلي فيها مختبر وتدريب عملي وأدوات — مش بس محاضرات.", weights: { R: 1 } },
    { id: "B5", type: "likert5", text_ar: "ما بتكتفي تعرف «شو» صار — بدك تفهم «ليش» و«كيف» من جوّا.", weights: { I: 1 } },
    { id: "B6", type: "likert5", text_ar: "المجالات يلي فيها بحث وتحليل وأرقام وداتا بتجذبك أكتر من غيرها.", weights: { I: 1, logic: 0.3 } },
    { id: "B7", type: "likert5", text_ar: "لما توقع بإيدك مشكلة معقدة — كود ما عم يشتغل، لغز، مسألة — بتحس بمتعة مش بضغط.", weights: { I: 1, prob: 0.3 } },
    { id: "B8", type: "likert5", text_ar: "حتى لو الكل حواليك مقتنع، ما بتقتنع إلا لما تشوف الدليل بنفسك.", weights: { I: 1 } },
    { id: "B9", type: "likert5", text_ar: "بتتحمس لما يكون قدامك مجال تبتكر فيه شي جديد ما حدا عمله قبلك بنفس الطريقة.", weights: { A: 1 } },
    { id: "B10", type: "likert5", text_ar: "المجالات يلي فيها تصميم أو كتابة أو تصوير أو موسيقى أو صناعة محتوى بتناديك.", weights: { A: 1 } },
    { id: "B11", type: "likert5", text_ar: "القوالب الجامدة وعبارة «هيك دايماً منعمل» بتضايقك.", weights: { A: 1, open: 0.3 } },
    { id: "B12", type: "likert5", text_ar: "بتميل للمشاريع المفتوحة يلي بتعطيك حرية تقرر كيف بتنفّذها.", weights: { A: 1, v_freedom: 0.3 } },
    { id: "B13", type: "likert5", text_ar: "لما تساعد شخص يفهم شغلة أو يتجاوز مشكلة، بتحس برضا حقيقي بيضل معك.", weights: { S: 1, v_help: 0.3 } },
    { id: "B14", type: "likert5", text_ar: "بتجذبك المجالات يلي هدفها مساعدة الناس: تعليمهم أو إرشادهم أو رعايتهم.", weights: { S: 1 } },
    { id: "B15", type: "likert5", text_ar: "بدك شغلك يكون إله أثر واضح بحياة ناس حقيقيين — مش بس أرقام على تقرير.", weights: { S: 1 } },
    { id: "B16", type: "likert5", text_ar: "بتحب الشغل يلي فيه تواصل إنساني يومي، مش قعدة لحالك طول النهار.", weights: { S: 1, extra: 0.3 } },
    { id: "B17", type: "likert5", text_ar: "بالاجتماعات أو بالقروب، بتحب تكون جزء من القرار — مش بس تنفّذ يلي بينقال.", weights: { E: 1 } },
    { id: "B18", type: "likert5", text_ar: "لما عندك فكرة، بتستمتع بلحظة إقناع الناس فيها — العرض، النقاش، الحماس.", weights: { E: 1, comm: 0.3 } },
    { id: "B19", type: "likert5", text_ar: "بتحب تكون أنت يلي بيبلّش: مبادرة، مشروع صغير، فكرة جديدة بالشلة أو الشغل.", weights: { E: 1 } },
    { id: "B20", type: "likert5", text_ar: "لما يكون في هدف أو تحدي أو منافسة، طاقتك بتعلى وبتشتغل أحسن.", weights: { E: 1, v_challenge: 0.3 } },
    { id: "B21", type: "likert5", text_ar: "بترتاح لما تكون المهام واضحة: شو المطلوب، لأيمتى، وكيف.", weights: { C: 1 } },
    { id: "B22", type: "likert5", text_ar: "بتحب الشغل يلي بدو دقة وترتيب ومتابعة — جداول، ملفات، تفاصيل مضبوطة.", weights: { C: 1, org: 0.3 } },
    { id: "B23", type: "likert5", text_ar: "بتقدر تتابع التفاصيل الصغيرة بدون ما تملّ — مراجعة، تدقيق، ترتيب داتا.", weights: { C: 1 } },
    { id: "B24", type: "likert5", text_ar: "بتفضّل الشغل يلي إله نظام وخطوات واضحة على الشغل الفوضوي المفتوح.", weights: { C: 1 } },
    {
      id: "B25", type: "scenario", text_ar: "تخيّل حالك بمعرض توظيف أو يوم مهني كبير، وكل زاوية فيه بتمثّل مجال مختلف. أي زاوية بتروح عليها أول شي؟",
      options: [
      { id: "B25o1", label_ar: "أجهزة وتجارب وأدوات بتنلمس بالإيد.", weights: { R: 2 } },
      { id: "B25o2", label_ar: "مختبر وبحث علمي وداتا.", weights: { I: 2 } },
      { id: "B25o3", label_ar: "تصميم وتصوير وإعلام وصناعة محتوى.", weights: { A: 2 } },
      { id: "B25o4", label_ar: "مهن بتساعد الناس: صحة، تعليم، دعم.", weights: { S: 2 } },
      { id: "B25o5", label_ar: "ريادة أعمال وتسويق وقيادة.", weights: { E: 2 } },
      { id: "B25o6", label_ar: "محاسبة وإدارة وأنظمة وتنظيم.", weights: { C: 2 } },
      ],
    },
    {
      id: "B26", type: "scenario", text_ar: "بأي مشروع جماعي — دراسة أو شغل أو تطوع — أي جزء بيشدّك أكتر شي؟",
      options: [
      { id: "B26o1", label_ar: "تنفيذ الشغل العملي والتجربة.", weights: { R: 2 } },
      { id: "B26o2", label_ar: "البحث والتحليل وجمع المعلومات.", weights: { I: 2 } },
      { id: "B26o3", label_ar: "تصميم العرض والشكل النهائي.", weights: { A: 2 } },
      { id: "B26o4", label_ar: "شرح الفكرة للناس والتواصل معهن.", weights: { S: 2 } },
      { id: "B26o5", label_ar: "قيادة الفريق وتوزيع الأدوار.", weights: { E: 2 } },
      { id: "B26o6", label_ar: "تنظيم الوقت والملفات والمتابعة.", weights: { C: 2 } },
      ],
    },
    {
      id: "B27", type: "scenario", text_ar: "لو طلعتلك فرصة شغل صيفي أو شغل جانبي (بارت تايم / فريلانس)، أي نوع بتختار؟",
      options: [
      { id: "B27o1", label_ar: "شغل فيه حركة وتطبيق بالإيد.", weights: { R: 2 } },
      { id: "B27o2", label_ar: "شغل فيه بحث وأرقام وتحليل.", weights: { I: 2 } },
      { id: "B27o3", label_ar: "شغل فيه تصميم أو صناعة محتوى.", weights: { A: 2 } },
      { id: "B27o4", label_ar: "شغل فيه تعليم أو رعاية أو مساعدة ناس.", weights: { S: 2 } },
      { id: "B27o5", label_ar: "شغل فيه بيع أو تسويق أو تنظيم فعاليات.", weights: { E: 2 } },
      { id: "B27o6", label_ar: "شغل فيه بيانات وملفات ومتابعة دقيقة.", weights: { C: 2 } },
      ],
    },
    {
      id: "B28", type: "pair", text_ar: "لو خيّروك بين تنتين، أيّاهن أقرب لقلبك؟",
      options: [
      { id: "B28o1", label_ar: "ساعد الناس وافهمهن، حتى لو ما كنت أنا القائد.", weights: { S: 2 } },
      { id: "B28o2", label_ar: "أثّر وقود وحرّك الناس نحو هدف واضح.", weights: { E: 2 } },
      ],
    },
    {
      id: "B29", type: "pair", text_ar: "وهون كمان، أيّاهن أقرب لك؟",
      options: [
      { id: "B29o1", label_ar: "افهم النظام والمنطق والدليل ورا الأشياء.", weights: { I: 2 } },
      { id: "B29o2", label_ar: "ابتكر فكرة جديدة أو طريقة مختلفة للتعبير.", weights: { A: 2 } },
      ],
    },
    {
      id: "B30", type: "pair", text_ar: "وآخر وحدة، أيّاهن أقرب لك؟",
      options: [
      { id: "B30o1", label_ar: "طبّق وجرّب وشوف النتيجة بإيدي.", weights: { R: 2 } },
      { id: "B30o2", label_ar: "فكّر وحلّل وافهم السبب قبل ما طبّق.", weights: { I: 2 } },
      ],
    },
  ],
};

// ---- Section C — الشخصية (32 items) ----
const SECTION_C: Section = {
  id: "C",
  title_ar: "الشخصية",
  intro_ar: "المحور الثالث: مين أنت من جوا؟ — مو تشخيص، خريطة. لتعرف أي بيئة دراسة وشغل بتناسب طبيعتك.",
  items: [
    { id: "C1", type: "likert5", text_ar: "لما ينفتح قدامك باب جديد — كورس، تجربة، مكان ما رحته قبل — بتجرّب حتى لو النتيجة مش مضمونة.", weights: { open: 1 } },
    { id: "C2", type: "likert5", text_ar: "لما تكتشف مجال جديد ما بتعرف عنه شي، بتحس بحماس مش بخوف.", weights: { open: 1 } },
    { id: "C3", type: "likert5", text_ar: "بتحب تفكر بطرق غير تقليدية — حتى لو حلّك مختلف عن يلي الكل متعوّد عليه.", weights: { open: 1 } },
    { id: "C4", type: "likert5", text_ar: "بين طريق واضح ومضمون وبين تجربة جديدة، بتختار المضمون غالباً.", weights: { open: 1 }, reverse: true },
    { id: "C5", type: "likert5", text_ar: "حتى لو المادة أو المهمة مش محمّستك أبداً، بتقدر تكمّلها للآخر.", weights: { consc: 1 } },
    { id: "C6", type: "likert5", text_ar: "مواعيدك وواجباتك — تسليم، دوام، وعد لحدا — بتلتزم فيها غالباً.", weights: { consc: 1 } },
    { id: "C7", type: "likert5", text_ar: "لما توقع عليك مهمة كبيرة، بتعرف تقسّمها لخطوات صغيرة وتبلّش فيها.", weights: { consc: 1, org: 0.5 } },
    { id: "C8", type: "likert5", text_ar: "بتأجّل وبتأجّل لحتى تتراكم عليك الشغلات وتلاقي حالك تحت الضغط.", weights: { consc: 1 }, reverse: true },
    { id: "V3", type: "likert5", text_ar: "أي درس أو مهارة جديدة، بتفهمها دايماً من أول مرة بدون ما ترجع فيها أبداً.", validity: "desirability" },
    { id: "C9", type: "likert5", text_ar: "بعد جمعة أو سهرة مع ناس، بترجع عالبيت مشحون طاقة مش مستنزَف.", weights: { extra: 1 } },
    { id: "C10", type: "likert5", text_ar: "بمكان جديد — دورة، شغل، مناسبة — غالباً أنت يلي بتبلّش الحديث مع ناس ما بتعرفهن.", weights: { extra: 1 } },
    { id: "C11", type: "likert5", text_ar: "النقاش الجماعي والشغل ضمن فريق بيحمّسك أكتر من الشغل لحالك.", weights: { extra: 1, team: 0.5 } },
    { id: "C12", type: "likert5", text_ar: "بتفضّل تعبّر عن أفكارك بالكتابة — رسالة، منشور — أكتر ما تحكي قدام الناس.", weights: { extra: 1 }, reverse: true },
    { id: "C13", type: "likert5", text_ar: "قبل ما تحكم على تصرف حدا، بتحاول أولاً تفهم شو حاسس وشو ظرفه.", weights: { empath: 1 } },
    { id: "C14", type: "likert5", text_ar: "لما تساعد شخص يطلع من مشكلة، بتحس برضا حقيقي حتى لو ما حدا عرف.", weights: { empath: 1 } },
    { id: "C15", type: "likert5", text_ar: "بتقدر تشتغل مع ناس مختلفين عنك كتير — بالأفكار والطباع — طالما في احترام.", weights: { empath: 1, team: 0.5 } },
    { id: "C16", type: "likert5", text_ar: "إذا كنت متأكد إنك عالحق، ما بيهمك كتير شعور الطرف التاني.", weights: { empath: 1 }, reverse: true },
    { id: "C17", type: "likert5", text_ar: "لما الأمور تصعب — سنة دراسية تقيلة، شغل ضاغط — بتحاول تكمّل مش تنسحب.", weights: { resil: 1, persist: 0.5 } },
    { id: "C18", type: "likert5", text_ar: "ليلة الامتحان أو التسليم، بتقدر تركّز نسبياً حتى تحت الضغط.", weights: { resil: 1 } },
    { id: "C19", type: "likert5", text_ar: "الموقف المزعج بيخلص… بس أنت بتضل عايشه براسك ساعات وأيام.", weights: { resil: 1 }, reverse: true },
    { id: "C20", type: "likert5", text_ar: "إذا خطتك الأولى ما مشيت، بتقدر تفكر بخطة بديلة بدل ما تجمد.", weights: { resil: 1, prob: 0.5 } },
    { id: "V4", type: "likert5", text_ar: "النقد ما بيأثر فيك أبداً — مهما كان قاسي ومن مين ما كان.", validity: "desirability" },
    { id: "C21", type: "likert5", text_ar: "بجلسة عيلة أو شلة، بتقدر تقول رأيك حتى لو كان مختلف عن رأي الكل.", weights: { autonomy: 1 } },
    { id: "C22", type: "likert5", text_ar: "بتسمع نصايح الناس يلي بتحبهن، بس ما بتلغي صوتك الداخلي كرمالهن.", weights: { autonomy: 1 } },
    { id: "C23", type: "likert5", text_ar: "إذا خالفك شخص قوي أو مهم عندك، بتغيّر رأيك بسرعة.", weights: { autonomy: 1 }, reverse: true },
    { id: "C24", type: "likert5", text_ar: "بتخاف تختار يلي بدك ياه إذا كان رح يزعّل أهلك.", weights: { autonomy: 1, flag_parent: 0.5 }, reverse: true },
    { id: "C25", type: "likert5", text_ar: "مقتنع إنك بتقدر تطوّر حالك بأي شي إذا تدرّبت وتعلّمت — الموهبة مش كل شي.", weights: { conf: 1 } },
    { id: "C26", type: "likert5", text_ar: "بتقدر تجرّب شي جديد قدام الناس حتى لو مش ممتاز فيه من البداية.", weights: { conf: 1 } },
    { id: "C27", type: "likert5", text_ar: "بتأجّل تبلّش أحياناً لأنك بدك النتيجة تطلع مثالية من أول مرة.", weights: { conf: 1 }, reverse: true },
    { id: "C28", type: "likert5", text_ar: "الخوف من النقد أو التعليقات ممكن يمنعك تعرض أفكارك أو شغلك.", weights: { conf: 1 }, reverse: true },
    {
      id: "C29", type: "scenario", text_ar: "اشتغلت على مشروع أو عرض تعبت عليه، وسلّمته، وإجاك الرد: «الشغل بدو تعديل كبير». كيف بتتصرف غالباً؟",
      options: [
      { id: "C29o1", label_ar: "بسأل عن الملاحظات بالتفصيل وبعتبرها فرصة أطوّر شغلي.", weights: { conf: 2, open: 1 } },
      { id: "C29o2", label_ar: "بتضايق وبحس إني مش منيح، وبضل مفكّر فيها طويل.", weights: { resil: -1.5, conf: -1 } },
      { id: "C29o3", label_ar: "بدافع عن شغلي فوراً قبل حتى ما اسمع التفاصيل.", weights: { autonomy: 0.5, empath: -1 } },
      { id: "C29o4", label_ar: "بتقبّل التعديل، بس بحتاج وقت لحتى ارتاح وارجع.", weights: { resil: 1 } },
      { id: "C29o5", label_ar: "بصير اتجنّب إني قدّم شغلي لقدّام لحتى ما انتقد.", weights: { conf: -2 } },
      ],
    },
    {
      id: "C30", type: "scenario", text_ar: "أهلك حاطين ببالهن تخصص أو مسار معيّن إلك، بس قلبك بمكان تاني. شو بتعمل غالباً؟",
      options: [
      { id: "C30o1", label_ar: "بشرحلهن رغبتي وبفتح معهن حوار هادي.", weights: { autonomy: 2, comm: 1 } },
      { id: "C30o2", label_ar: "بجمع معلومات وبحضّر أسباب مقنعة وبعدين بناقشهن.", weights: { autonomy: 2, logic: 1 } },
      { id: "C30o3", label_ar: "بتنازل فوراً لحتى ما ازعّلهن.", weights: { autonomy: -2, flag_parent: 2 } },
      { id: "C30o4", label_ar: "بخبّي رغبتي وبمشي مع رأيهن بالظاهر.", weights: { autonomy: -1.5, flag_parent: 2 } },
      { id: "C30o5", label_ar: "بتمسّك برأيي بدون ما اسمع وجهة نظرهن.", weights: { autonomy: 1, empath: -1 } },
      { id: "C30o6", label_ar: "بتشتّت وما بعود عارف شو اختار.", weights: { flag_anxiety: 2 } },
      ],
    },
  ],
};

// ---- Section D — المهارات الحالية (18 items) ----
const SECTION_D: Section = {
  id: "D",
  title_ar: "المهارات الحالية",
  intro_ar: "المحور الرابع: شو بتعرف تعمل اليوم؟ — المهارة مو موهبة ثابتة، هي عضلة. منقيس وضعها الحالي لنعرف شو منقوّي.",
  note_ar: "جاوب عن وضعك الحالي فعلاً — مو عن قدرتك النظرية. المهارة المنخفضة اليوم مو عيب، هي بند بخطة التطوير.",
  items: [
    { id: "D1", type: "likert5", text_ar: "لما تحكي أو تكتب — رسالة، إيميل، عرض — فكرتك بتوصل واضحة من أول مرة.", weights: { comm: 1 } },
    { id: "D2", type: "likert5", text_ar: "لما حدا يحكيلك مشكلته، بتسمعه للآخر قبل ما ترد أو تعطي رأيك.", weights: { comm: 1, empath: 0.3 } },
    { id: "D3", type: "likert5", text_ar: "بتنظّم وقتك بحيث تخلّص المطلوب بدون فوضى وسهر الليلة الأخيرة.", weights: { org: 1 } },
    { id: "D4", type: "likert5", text_ar: "ملاحظاتك وملفاتك وأغراضك — عالموبايل واللابتوب وبالشنتة — مرتبة وبتلاقيها بسرعة.", weights: { org: 1 } },
    { id: "D5", type: "likert5", text_ar: "لما توقف قدامك مشكلة، بتجرّب أكتر من طريقة قبل ما تقول «خلص، ما في حل».", weights: { prob: 1, persist: 0.3 } },
    { id: "D6", type: "likert5", text_ar: "المشكلة الكبيرة المعقدة بتعرف تفكفكها لأجزاء صغيرة وتحلها وحدة وحدة.", weights: { prob: 1, logic: 0.3 } },
    { id: "V2", type: "likert5", text_ar: "سؤال تأكيد: لنتأكد من الانتباه، اختار هون «لا يشبهني أبداً».", validity: "attention:1" },
    { id: "D7", type: "likert5", text_ar: "بالفريق، بتعرف شو دورك وبتنجزه بدون ما حدا يلحقك أو يضغط عليك.", weights: { team: 1 } },
    { id: "D8", type: "likert5", text_ar: "إذا صار خلاف جوّا الفريق، بتقدر تتعامل معه بدون ما يوقف الشغل.", weights: { team: 1, social: 0.3 } },
    { id: "D9", type: "likert5", text_ar: "بتقدر تعلّم حالك مهارة جديدة لحالك — من يوتيوب أو كورس أونلاين أو كتاب.", weights: { selflearn: 1 } },
    { id: "D10", type: "likert5", text_ar: "لما ما تفهم شي، بتدوّر عليه بنفسك بدل ما تستنى حدا يشرحلك ياه.", weights: { selflearn: 1 } },
    { id: "D11", type: "likert5", text_ar: "بتتعلّم استخدام البرامج والتطبيقات الجديدة بسرعة.", weights: { digital: 1 } },
    { id: "D12", type: "likert5", text_ar: "بتستخدم التكنولوجيا لتخدم دراستك وشغلك وأهدافك — مش بس للتسلية والسحب.", weights: { digital: 1 } },
    { id: "V8", type: "likert5", text_ar: "لما تفهم فكرة جديدة، غالباً بيصعب عليك تشرحها لغيرك بطريقة أبسط.", validity: "consistency:A2" },
    { id: "D13", type: "likert5", text_ar: "لما يلزمك قرار، بتجمع معلومات كافية وبتقرر — بدون تأجيل طويل.", weights: { decide: 1 } },
    { id: "D14", type: "likert5", text_ar: "لما يطلع قرارك غير موفّق، بتتحمّل نتيجته بدون ما تلوم غيرك.", weights: { decide: 1, ready_own: 0.5 } },
    { id: "D15", type: "likert5", text_ar: "بتكمّل يلي بلّشته حتى بعد ما يروح حماس الأيام الأولى.", weights: { persist: 1 } },
    { id: "D16", type: "likert5", text_ar: "بتقدر تلتزم بتدريب ممل أو متكرر إذا كان ضروري لهدفك.", weights: { persist: 1, consc: 0.3 } },
  ],
};

// Shared framing line for every importance5 item (items are shown one per screen,
// so each needs its own question stem above the statement).
const IMPORTANCE_STEM_AR = "قديش هالشي مهم عندك وأنت عم تختار مستقبلك؟";

// ---- Section E — القيم والدوافع (12 items) ----
const SECTION_E: Section = {
  id: "E",
  title_ar: "القيم والدوافع",
  intro_ar: "المحور الخامس: شو بيحركك؟ — التخصص الناجح هو يلي بيشبه قيمك، مو بس قدراتك.",
  items: [
    { id: "E1", type: "importance5", stem_ar: IMPORTANCE_STEM_AR, text_ar: "وظيفة مستقرة وآمنة: معاش ثابت، وما في خوف من بكرا.", weights: { v_security: 1 } },
    { id: "E2", type: "importance5", stem_ar: IMPORTANCE_STEM_AR, text_ar: "دخل مادي منيح: تقدر تعيش مرتاح وتحقق يلي بدك ياه.", weights: { v_income: 1 } },
    { id: "E3", type: "importance5", stem_ar: IMPORTANCE_STEM_AR, text_ar: "مكانة اجتماعية واحترام: لما ينسأل «شو بتشتغل؟» يكون الجواب إله وزن.", weights: { v_status: 1, flag_status: 0.5 } },
    { id: "E4", type: "importance5", stem_ar: IMPORTANCE_STEM_AR, text_ar: "مساعدة الناس: تحس إنك تركت أثر حقيقي بحياة حدا.", weights: { v_help: 1 } },
    { id: "E5", type: "importance5", stem_ar: IMPORTANCE_STEM_AR, text_ar: "الحرية والاستقلال: تشتغل بطريقتك وعلى وقتك، بدون حدا فوق راسك بكل تفصيلة.", weights: { v_freedom: 1 } },
    { id: "E6", type: "importance5", stem_ar: IMPORTANCE_STEM_AR, text_ar: "الإبداع والتعبير: مساحة تطلع فيها أفكارك وبصمتك الخاصة.", weights: { v_creative: 1 } },
    { id: "E7", type: "importance5", stem_ar: IMPORTANCE_STEM_AR, text_ar: "القيادة والتأثير: تكون صاحب قرار وناس بتمشي وراك.", weights: { v_lead: 1 } },
    { id: "E8", type: "importance5", stem_ar: IMPORTANCE_STEM_AR, text_ar: "المعرفة والتعلم المستمر: كل يوم بتتعلم شي جديد وبتتعمق أكتر.", weights: { v_know: 1 } },
    { id: "E9", type: "importance5", stem_ar: IMPORTANCE_STEM_AR, text_ar: "الراحة النفسية والتوازن: شغل ما بياكل حياتك، وفي وقت إلك ولأهلك ولهواياتك.", weights: { v_comfort: 1 } },
    { id: "E10", type: "importance5", stem_ar: IMPORTANCE_STEM_AR, text_ar: "التحدي والإنجاز: أهداف صعبة ومنافسة بتحسّسك إنك عايش.", weights: { v_challenge: 1 } },
    { id: "V7", type: "likert5", text_ar: "غالباً بتترك المهام بنص الطريق إذا راح حماسك تجاهها.", validity: "consistency:C5" },
    {
      id: "E11", type: "top3", max_select: 3,
      text_ar: "لو وقفت اليوم قدام قرار مصيري بمستقبلك، أي 3 قيم من هدول ما فيك تتنازل عنهن؟",
      options: [
        { id: "E11o1", label_ar: "الاستقرار", weights: { v_security: 2 } },
        { id: "E11o2", label_ar: "الدخل", weights: { v_income: 2 } },
        { id: "E11o3", label_ar: "المكانة", weights: { v_status: 2 } },
        { id: "E11o4", label_ar: "مساعدة الناس", weights: { v_help: 2 } },
        { id: "E11o5", label_ar: "الحرية", weights: { v_freedom: 2 } },
        { id: "E11o6", label_ar: "الإبداع", weights: { v_creative: 2 } },
        { id: "E11o7", label_ar: "القيادة والتأثير", weights: { v_lead: 2 } },
        { id: "E11o8", label_ar: "المعرفة", weights: { v_know: 2 } },
        { id: "E11o9", label_ar: "الراحة والتوازن", weights: { v_comfort: 2 } },
        { id: "E11o10", label_ar: "التحدي", weights: { v_challenge: 2 } },
      ],
    },
  ],
};

// ---- Section F — نمط التعلم (10 items) ----
const SECTION_F: Section = {
  id: "F",
  title_ar: "نمط التعلم",
  intro_ar: "المحور السادس: كيف بتتعلم أحسن؟ — لأنه نفس المعلومة بتوصل لكل عقل بطريقة مختلفة.",
  items: [
    { id: "F1", type: "likert5", text_ar: "لما بدك تفهم موضوع، الشرح المحكي أو المقروء الواضح بيوصّلك أكتر من أي شي تاني.", weights: { l_verbal: 1 } },
    { id: "F2", type: "likert5", text_ar: "بتحفظ أحسن لما تقرا بصوت عالي أو تسمع تسجيل وتكرره.", weights: { l_verbal: 1 } },
    { id: "F3", type: "likert5", text_ar: "خريطة ذهنية أو رسمة بتفهّمك أكتر من صفحة نص طويلة.", weights: { l_visual: 1 } },
    { id: "F4", type: "likert5", text_ar: "بالامتحان بتتذكر وين كانت المعلومة بالصفحة وشكلها — يمين، شمال، بالجدول.", weights: { l_visual: 1 } },
    { id: "V5", type: "likert5", text_ar: "ما بتحس بالملل أبداً وأنت عم تدرس — ولا للحظة.", validity: "desirability" },
    { id: "F5", type: "likert5", text_ar: "الفهم الحقيقي عندك بيصير لما تطبّق بإيدك وتجرّب بنفسك — مش بس تسمع.", weights: { l_practical: 1 } },
    { id: "F6", type: "likert5", text_ar: "الأمثلة والتمارين والمشاريع الصغيرة بتعلّمك أكتر من الشرح النظري.", weights: { l_practical: 1 } },
    { id: "F7", type: "likert5", text_ar: "لما تناقش المادة مع حدا أو تشرحها لغيرك، بتحس إنها ثبتت فعلاً.", weights: { l_social: 1 } },
    { id: "F8", type: "likert5", text_ar: "الدراسة مع مجموعة — بالمكتبة أو حتى أونلاين — بتحمّسك أكتر من الدراسة لحالك.", weights: { l_social: 1 } },
    { id: "V9", type: "likert5", text_ar: "الصور والمخططات ما بتساعدك كتير بالفهم.", validity: "consistency:A9" },
  ],
};

// ---- Section G — الجاهزية النفسية لاختيار التخصص (14 items) ----
const SECTION_G: Section = {
  id: "G",
  title_ar: "الجاهزية النفسية لاختيار التخصص",
  intro_ar: "المحور السابع: قديش أنت جاهز للقرار؟ — وهون بدنا صدقك أكتر من أي محور تاني.",
  note_ar: "هون ما في إجابات «أفضل من غيرها» إطلاقاً. الصدق هون هو يلي بيخلي توصياتك تنفعك فعلاً.",
  items: [
    { id: "G1", type: "likert5", text_ar: "لو سألك حدا اليوم «شو نقاط قوتك وضعفك؟» بتجاوب بوضوح، بدون لف ودوران.", weights: { ready_know: 1 } },
    { id: "G2", type: "likert5", text_ar: "التخصصات أو المسارات يلي عم تفكر فيها — بحثت عن تفاصيلها فعلياً، مش بس سمعت عنها.", weights: { ready_know: 1 } },
    { id: "G3", type: "likert5", text_ar: "بتعرف الفرق بين دراسة التخصص (المواد والسنين) وبين الشغل الحقيقي بالمهنة بعد التخرج.", weights: { ready_know: 1 } },
    { id: "G4", type: "likert5", text_ar: "بحثت حتى عن الجانب الصعب أو الممل بالمجال يلي بيعجبك — مش بس الصورة الحلوة عنه.", weights: { ready_know: 1 } },
    { id: "G5", type: "likert5", text_ar: "بتحس إنك ممكن تختار تخصص معين لأنه أهلك بدهن ياه — مش لأنك أنت بدك ياه.", weights: { flag_parent: 1 } },
    { id: "G6", type: "likert5", text_ar: "ممكن تختار تخصص لأنه رفقاتك رح يدرسوه وتضلوا سوا.", weights: { flag_peer: 1 } },
    { id: "V6", type: "likert5", text_ar: "بتخلّص كل مهمة بتبلّشها دايماً — بدون ولا تأجيل واحد.", validity: "desirability" },
    { id: "G7", type: "likert5", text_ar: "أحياناً صورة التخصص قدام الناس بتهمك أكتر من تفاصيله اليومية الحقيقية.", weights: { flag_status: 1 } },
    { id: "G8", type: "likert5", text_ar: "بتحب مادة معينة وبتفترض إنك أكيد رح تحب مهنتها — مع إنهن ممكن يكونوا شي مختلف تماماً.", weights: { flag_confusion: 1 } },
    { id: "G9", type: "likert5", text_ar: "خوفك من الاختيار الغلط كبير لدرجة إنه عم يمنعك تحسم.", weights: { flag_anxiety: 1 } },
    { id: "G10", type: "likert5", text_ar: "لو القرار إلك لحالك — بدون خوف ولا ضغط من حدا — يمكن تختار شي مختلف عن يلي ماشي فيه هلق.", weights: { flag_parent: 1 } },
    { id: "G11", type: "likert5", text_ar: "مستعد تجرّب التخصص قبل القرار النهائي: كورس قصير، مقابلة حدا شغال بالمجال، زيارة.", weights: { ready_own: 1 } },
    { id: "G12", type: "likert5", text_ar: "إذا اخترت وواجهت صعوبات، بتتحمّل مسؤولية قرارك.", weights: { ready_own: 1 } },
    { id: "V10", type: "likert5", text_ar: "بتنجز مهامك أول بأول وما بتخلي شي يتراكم عليك.", validity: "consistency:C8" },
  ],
};

// ---- Section H — لمحة عن تفكيرك المنطقي (8 items) ----
const SECTION_H: Section = {
  id: "H",
  title_ar: "لمحة عن تفكيرك المنطقي",
  intro_ar: "المحور الأخير: 8 أسئلة سريعة وممتعة تختبر بديهتك ومنطقك — ما في ضغط، ما في «نجح ورسب». جرّب تحلها بسرعة، ولاحظ متى دماغك يشتغل بأفضل صورة.",
  note_ar: "لكل سؤال إجابة واحدة صحيحة. اقرأ بهدوء ثم اختر — التسرّع بيوقعك بالفخ.",
  items: [
    {
      id: "H1", type: "iq",
      text_ar: "اشتريت هودي وكاب حقهن سوا 1.10$. الهودي أغلى من الكاب بـ 1.00$. قديش حق الكاب؟",
      correct: "H1o2",
      options: [
        { id: "H1o1", label_ar: "0.10$" },
        { id: "H1o2", label_ar: "0.05$" },
        { id: "H1o3", label_ar: "1.00$" },
        { id: "H1o4", label_ar: "0.15$" },
      ],
    },
    {
      id: "H2", type: "iq",
      text_ar: "حساب عالسوشال ميديا عدد متابعينه عم يتضاعف كل يوم. إذا وصل لهدفه الكامل بعد 48 يوم، بأي يوم كان واصل لنص الهدف؟",
      correct: "H2o2",
      options: [
        { id: "H2o1", label_ar: "24" },
        { id: "H2o2", label_ar: "47" },
        { id: "H2o3", label_ar: "12" },
        { id: "H2o4", label_ar: "46" },
      ],
    },
    {
      id: "H3", type: "iq",
      text_ar: "والدة سارة عندها 5 بنات: نانا، نيني، نونو، نينو… شو اسم البنت الخامسة؟",
      correct: "H3o2",
      options: [
        { id: "H3o1", label_ar: "نانو" },
        { id: "H3o2", label_ar: "سارة" },
        { id: "H3o3", label_ar: "ما فينا نعرف من المعطيات" },
        { id: "H3o4", label_ar: "نونا" },
      ],
    },
    {
      id: "H4", type: "iq",
      text_ar: "بسباق ركض — أو حتى بلعبة سباق أونلاين — تجاوزت الشخص يلي بالمركز الثاني. بأي مركز صرت هلق؟",
      correct: "H4o2",
      options: [
        { id: "H4o1", label_ar: "الأول" },
        { id: "H4o2", label_ar: "الثاني" },
        { id: "H4o3", label_ar: "الثالث" },
        { id: "H4o4", label_ar: "لا يمكن معرفة ذلك" },
      ],
    },
    {
      id: "H5", type: "iq",
      text_ar: "سامر عنده 4 أخوات و3 إخوة. أخته لينا — كم أخت عندها؟",
      correct: "H5o2",
      options: [
        { id: "H5o1", label_ar: "4" },
        { id: "H5o2", label_ar: "3" },
        { id: "H5o3", label_ar: "5" },
        { id: "H5o4", label_ar: "2" },
      ],
    },
    {
      id: "H6", type: "iq",
      text_ar: "قطعت الكهربا والبيت عتم وبرد، ومعك عود كبريت واحد بس. قدامك شمعة، وبابور غاز، وصوبيّة حطب. شو أول شي بتشعّل؟",
      correct: "H6o4",
      options: [
        { id: "H6o1", label_ar: "الشمعة" },
        { id: "H6o2", label_ar: "بابور الغاز" },
        { id: "H6o3", label_ar: "صوبيّة الحطب" },
        { id: "H6o4", label_ar: "عود الكبريت" },
      ],
    },
    {
      id: "H7", type: "iq",
      text_ar: "بشقة الطلاب: 3 كبسات كهربا برّا الغرفة، كل وحدة بتشغّل لمبة جوّا، وكلهن مطفيين. فيك تفوت عالغرفة مرة وحدة بس. شو أذكى طريقة تعرف أي كبسة لأي لمبة؟",
      correct: "H7o2",
      options: [
        { id: "H7o1", label_ar: "بشغّل كبسة وحدة بس وبفوت." },
        { id: "H7o2", label_ar: "بشغّل الأولى كم دقيقة، بطفّيها وبشغّل التانية، وبفوت: المضوية = التانية، المطفية الدافية = الأولى، المطفية الباردة = التالتة." },
        { id: "H7o3", label_ar: "بشغّل التلاتة سوا وبطفّي تنتين قبل ما فوت." },
        { id: "H7o4", label_ar: "مستحيل نعرف من فوتة وحدة." },
      ],
    },
    {
      id: "H8", type: "iq",
      text_ar: "إذا 5 طابعات بتطبع 5 ملازم بـ 5 دقايق، كم دقيقة بدها 100 طابعة لتطبع 100 ملزمة؟",
      correct: "H8o3",
      options: [
        { id: "H8o1", label_ar: "100" },
        { id: "H8o2", label_ar: "20" },
        { id: "H8o3", label_ar: "5" },
        { id: "H8o4", label_ar: "50" },
      ],
    },
  ],
};

export const SECTIONS: Section[] = [SECTION_A, SECTION_B, SECTION_C, SECTION_D, SECTION_E, SECTION_F, SECTION_G, SECTION_H];

// ---------- Landing / UX copy ----------
export const LANDING = {
  h1: "بوصلة د. سارة — اختبار تفاعلي لاكتشاف ذكاءاتك، شخصيتك، ميولك، والتخصص الجامعي الأقرب لك",
  sub: "قبل ما تختار تخصصك، اعرف نفسك. البوصلة ما بتقلك «شو لازم تصير»… بتفرجيك بصمتك الحالية: كيف تفكر، شو تحب، شو نقاط قوتك، وشو المسارات الأقرب لك — وبعدها القرار قرارك.",
  features: [
    "8 محاور علمية",
    "تقرير شخصي مفصّل",
    "خطة 90 يوماً قبل القرار النهائي",
  ],
  trust_line: "صمّمته د. سارة النحّاس وفق نماذج علمية معتمدة (الذكاءات المتعددة، أنماط الميول المهنية، سمات الشخصية) — وهو أداة توجيه تربوي، وليس تشخيصاً نفسياً.",
  parents_box: "لأهل الطالب: البوصلة ما بتقرر بدل ابنكم — بتساعده يسمع صوته الداخلي، وبتعطيكم لغة مشتركة تدعموه فيها بدون ضغط.",
};

// ---------- Single source of truth for the assessment duration ----------
export const DURATION = {
  minutes_min: 45,
  minutes_max: 60,
  label_ar: "45–60 دقيقة",
  label_en: "45–60 minutes",
  per_item_seconds: 18,
  per_item_seconds_h: 60,
} as const;

export const WELCOME_AR = `أهلاً فيك 🌿
هاد مو امتحان، وما في إجابة صح وغلط.
في بس إجابة تشبهك، وإجابة ما تشبهك.

عهد الصدق: جاوب متل ما أنت فعلاً اليوم — مو متل ما بتحب تكون، ومو متل ما بيتوقعوا منك.
كل ما كنت أصدق، كل ما كانت بوصلتك أدق.

المدة: ${DURATION.label_ar}. فيك توقف وتكمل بأي وقت — إجاباتك بتنحفظ لحالها.
إجاباتك سرية، والتقرير إلك أنت أولاً.`;

export const MIDBREAK_AR = "خلصت أطول ثلاث محاور 🎉 خذ نفس عميق… الباقي أخف وأسرع. كمّل وأنت مرتاح، أو ارجع بوقت تاني — كل شي محفوظ.";
export const COMPLETION_AR = "خلصنا 🌿 بوصلتك عم تنرسم هلق…\nتذكّر: النتيجة خريطة أولية، مو حكم نهائي. أنت أكبر من أي اختبار.";
export const DISCLAIMER_AR = "هذا التقرير أداة توجيه تربوي مبنية على إجاباتك الحالية — وهو ليس تشخيصاً نفسياً ولا حكماً نهائياً. بصمتك قابلة للنمو والتغيير.";

// ---------- Report fixed content (Part 8) ----------
export const VALUE_ONELINERS: Record<string, string> = {
  v_security:  "يريحك أن تكون بيئة عملك مستقرة وواضحة الملامح.",
  v_income:    "يهمك أن يعطيك عملك دخلاً يوفّر لك خياراتك المستقبلية.",
  v_status:    "تحب أن يكون لعملك حضور محترم أمام الناس ومعنى اجتماعي واضح.",
  v_help:      "يريحك أن يكون لجهدك أثر مباشر في حياة الناس.",
  v_freedom:   "تحب أن تختار طريقتك وإيقاعك في العمل دون قيود جامدة.",
  v_creative:  "تنتعش عندما تخلق شيئاً جديداً أو تعبّر عن رؤيتك.",
  v_lead:      "يجذبك أن تكون صوتاً مؤثراً وقائداً لخطوة أو فريق.",
  v_know:      "يشعل حماسك أن تتعلم وتفهم وتغوص عميقاً في المعرفة.",
  v_comfort:   "تقدّر أن يبقى لعملك حدود صحية تحمي راحتك النفسية.",
  v_challenge: "تحب التحدي والمنافسة، والإنجاز الملموس يعطيك طاقتك.",
};

export const LEARNING_TIPS: Record<string, string[]> = {
  l_verbal: [
    "استمع للدرس مرتين ثم لخّصه بصوت مسموع.",
    "استخدم بودكاست/شرح مسجّل لتغطية أول قراءة.",
  ],
  l_visual: [
    "حوّل ملخصاتك إلى خرائط ذهنية.",
    "ذاكر بالألوان والرموز وأماكن ثابتة على الصفحة.",
  ],
  l_practical: [
    "بعد كل درس، طبّق مثالاً أو مسألة صغيرة فوراً.",
    "اربط كل فكرة بمشروع صغير أو تجربة يدوية.",
  ],
  l_social: [
    "اشرح الدرس لصديق كأنك أستاذه.",
    "ذاكر أحياناً ضمن مجموعة صغيرة تتبادل الأسئلة.",
  ],
};

export const FLAG_PARAGRAPHS: Record<string, string> = {
  flag_parent:    "ظهر في إجاباتك أن رأي أهلك له وزن كبير في اختيارك. حبهم لك حقيقي، وصوتك الداخلي حقيقي أيضاً — والتقرير هذا قد يكون بداية حوار هادئ بينكما، لا سلاحاً في نقاش.",
  flag_peer:      "ظهر أن اختيار أصدقائك يؤثر في ميلك. رفقتهم جميلة، لكن مسارك الجامعي طويل — اختر ما يشبهك أنت، حتى لو افترقت الطرق مؤقتاً.",
  flag_status:    "ظهر أن صورة التخصص أمام الناس تجذبك أحياناً أكثر من تفاصيله اليومية. جرّب أن ترى المهنة من الداخل قبل الحسم — الصورة اللامعة لا تعكس دائماً اليوم العملي.",
  flag_confusion: "لاحظنا أنك تخلط أحياناً بين حبك لمادة دراسية وبين المهنة التي تنتظرك بعدها. المادة في الصف شيء، والعمل الميداني بعد التخرج شيء آخر — قابل من يعيش المهنة قبل القرار.",
  flag_anxiety:   "ظهر عليك قلق واضح من القرار. القلق مشروع لأن الاختيار حقيقي — لكن خطة الـ90 يوماً في أسفل التقرير مصممة لتحويل هذا القلق إلى خطوات صغيرة قابلة للتنفيذ.",
};

export const CONFIDENCE_TEXTS = {
  high:   "إجاباتك كانت متسقة، ويمكنك الاعتماد على هذه الخريطة كنقطة انطلاق قوية.",
  medium: "ظهرت بعض الفروقات بين إجاباتك، لذلك اقرأ النتيجة كخريطة أولية وناقشها مع شخص يعرفك جيداً.",
  low:    "ظهرت فروقات ملحوظة في نمط الإجابة (سرعة أو تشابه أو تعارض). ننصحك بإعادة الاختبار في وقت هادئ — النسخة الأدق تستحق.",
};

export const PARENT_LETTER = {
  opening: "ابنكم لا يكتشف مستقبله من سؤال واحد: «ماذا تريد أن تصبح؟» — هو يحتاج تجارب صغيرة، وملاحظات صادقة، وحواراً آمناً حتى يرى نفسه بوضوح. لا تضغطوا عليه ليختار بسرعة، ولا تُسقطوا عليه أحلامكم، ولا تحكموا على ميوله من علامة مدرسية واحدة.",
  dialog_questions_ar: [
    "ماذا يعجبك في هذا التخصص؟",
    "ماذا تعرف عن صعوباته؟",
    "هل تحب المهنة أم صورتها؟",
    "ماذا تحتاج حتى تجرّبها؟",
    "كيف نساعدك دون أن نقرر بدلاً عنك؟",
  ],
  parent_pressure_paragraph: "لاحظنا أن ابنكم يزن رأيكم كثيراً — وهذا دليل احترام، لكنه أحياناً يخفي صوته الحقيقي. المراهق لا يختار مستقبله بوضوح عندما يشعر أنه يجب أن يرضي الجميع؛ هو يحتاج أن يسمع صوته الداخلي دون أن يفقد دعمكم.",
  closing_plan_intro: "خطة 90 يوماً مشتركة — رافقوه فيها دون أن تقودوها عنه.",
};

export const PLAN_90_TEMPLATE = [
  "1. احضر 3 محاضرات أو فيديوهات معمّقة عن: {major1}، {major2}، {major3}.",
  "2. قابل شخصين يعملان فعلاً في المجال الأقرب لقلبك.",
  "3. جرّب كورساً قصيراً في {major1}.",
  "4. نفّذ مشروعاً صغيراً بيدك.",
  "5. اكتب يومياتك: متى تحمّست؟ متى مللت؟",
  "6. راقب: هل تحب المهنة أم صورتها؟",
  "7. ناقش هذا التقرير مع أهلك بهدوء — ومع شخص يعرفك جيداً.",
];

// ---------- Tier gating map (Part 8.4) ----------
// Bousala v1 is purchase-only. Free tier intentionally omitted;
// the shared "free"|"full"|"premium" union stays for other instruments.
export const TIERS = {
  full: {
    label_ar: "التقرير الكامل",
    price_usd: null as number | null,
    sections: [
      "fingerprint", "top_intelligences", "holland_code",
      "personality", "skills", "values", "learning", "readiness",
      "majors", "careers", "plan_90", "confidence", "parent_letter",
    ],
  },
  premium: {
    label_ar: "تقرير + جلسة تفسير",
    price_usd: null as number | null,
    sections: [
      "fingerprint", "top_intelligences", "holland_code",
      "personality", "skills", "values", "learning", "readiness",
      "majors", "careers", "plan_90", "confidence", "parent_letter",
      "pdf_export", "interpretation_session_cta",
    ],
  },
};

// ---------- AI narrative (Part 8.5) — server fn will call Lovable AI Gateway ----------
export const AI_NARRATIVE = {
  prompt_version: "bousala-narrative-v1",
  temperature: 0,
  system_prompt: `أنت محرّر تقارير «بوصلة د. سارة». مهمتك الوحيدة: تحويل نتائج رقمية محسوبة مسبقاً إلى فقرات عربية دافئة.
قواعد صارمة لا استثناء فيها:
1. اكتب فقط عن البيانات الموجودة في JSON المُدخل. يُمنع اختراع أي رقم أو سمة أو ذكاء أو تخصص أو مهنة غير مذكورة فيه.
2. لا تشخيص نفسي، لا مصطلحات إكلينيكية، لا أحكام. ممنوع نهائياً: فاشل، ضعيف، لا تصلح، غبي، اضطراب، مريض.
3. لغة النمو دائماً: «بحسب إجاباتك الحالية…»، «قابل للتطوير…»، «لا يظهر كخيار أول الآن…».
4. خاطب الطالب بـ«أنت» باحترام ودفء، بعربية فصيحة مبسطة قريبة من القلب، بأسلوب د. سارة: حنون، عميق، غير متعالٍ، غير واعظ.
5. في فقرة الأهل: لا تلُم الأهل أبداً، ولا تطلب من الطالب تجاهلهم.
6. أخرِج JSON فقط بالمفاتيح المطلوبة وبحدود الكلمات المحددة، دون أي نص خارج JSON.
المفاتيح المطلوبة: fingerprint_desc (60–90 كلمة)، personality_paragraph (80–120)، skills_paragraph (40–70)، majors_intro (30–50)، parent_profile (80–120).`,
  fallbacks: {
    fingerprint_desc:      "هذه بصمتك الحالية بحسب إجاباتك اليوم — مزيج من طاقتك في التفكير، وشغفك بالتفاعل، وطريقتك في رؤية العالم. البصمة قابلة للنمو، وهذا التقرير نقطة انطلاق لا حكم نهائي.",
      personality_paragraph: "ملامح شخصيتك اليوم تعكس مزيجاً متوازناً من الجوانب التي تلمع فيها والجوانب القابلة للتطوير. اقرأ الشرائط أعلاه كخريطة، وابدأ بتقوية جانب واحد في كل فصل دراسي.",
      skills_paragraph:      "لديك مهارات قوية يمكنك البناء عليها، ومهارات أخرى تحتاج تدريباً منتظماً. المهارة عضلة تنمو بالتكرار، لا سمة ثابتة.",
      majors_intro:          "هذه ليست قائمة أحكام، بل خريطة قرب. اقرأها بترتيبها، وجرّب الأقرب لقلبك قبل الحسم.",
      parent_profile:        "ابنكم يمر بمرحلة يبني فيها صوته الداخلي. دعموه بحوار هادئ، وأتاحوا له تجربة اهتماماته عن قرب قبل القرار النهائي.",
  },
};

// Ethical forbidden words list (validator will reject AI outputs that contain any).
export const FORBIDDEN_WORDS = ["فاشل", "ضعيف", "لا تصلح", "غبي", "اضطراب", "مريض", "غير قادر", "كسول"];

// ---------- Full config object stored in instruments.config ----------
export const BOUSALA_V1_CONFIG = {
  version: 1,
  estimated_minutes: 50,
  landing: LANDING,
  welcome_ar: WELCOME_AR,
  midbreak_ar: MIDBREAK_AR,
  midbreak_after_section: "C",
  completion_ar: COMPLETION_AR,
  disclaimer_ar: DISCLAIMER_AR,
  likert_labels: LIKERT_LABELS,
  importance_labels: IMPORTANCE_LABELS,
  scales: SCALES,
  pre_profile: PRE_PROFILE,
  sections: SECTIONS,
  value_oneliners: VALUE_ONELINERS,
  learning_tips: LEARNING_TIPS,
  flag_paragraphs: FLAG_PARAGRAPHS,
  confidence_texts: CONFIDENCE_TEXTS,
  parent_letter: PARENT_LETTER,
  plan_90_template: PLAN_90_TEMPLATE,
  tiers: TIERS,
  ai_narrative: AI_NARRATIVE,
  forbidden_words: FORBIDDEN_WORDS,
  cluster_careers: null as null | Record<string, string>, // filled at seed time from CLUSTER_CAREERS_AR
  fingerprint_map: { R: "العملي", I: "المحلل", A: "المبدع", S: "الاجتماعي", E: "القيادي", C: "المنظم" },
  personality_bands: [
    { min: 65, max: 100, label_ar: "مرتفع" },
    { min: 40, max: 64,  label_ar: "متوسط" },
    { min: 0,  max: 39,  label_ar: "يحتاج تطوير" },
  ],
};